import __var_0__ from '@stdlib/math/base/assert/is-nan';
/**

*
* Copyright (c) 2020 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //
var isnan = __var_0__;


// MAIN //

/**
* Calculates the range of a strided array via a callback function, ignoring `NaN` values.
*
* @param {PositiveInteger} N - number of indexed elements
* @param {Collection} x - input array/collection
* @param {integer} stride - index increment
* @param {NonNegativeInteger} offset - starting index
* @param {Callback} clbk - callback
* @param {*} [thisArg] - execution context
* @returns {number} range
*
* @example
* var x = [ -2.0, 1.0, 3.0, -5.0, 4.0, NaN, 0.0, -1.0, -3.0 ];
*
* function accessor( v ) {
*     return v * 2.0;
* }
*
* var v = nanrangeBy( x.length, x, 1, 0, accessor );
* // returns 18.0
*/
function nanrangeBy( N, x, stride, offset, clbk, thisArg ) {
	var max;
	var min;
	var ix;
	var v;
	var i;

	if ( N <= 0 ) {
		return NaN;
	}
	if ( N === 1 || stride === 0 ) {
		v = clbk.call( thisArg, x[ 0 ], 0, 0, x );
		if ( v === void 0 || isnan( v ) ) {
			return NaN;
		}
		return 0.0;
	}
	ix = offset;
	for ( i = 0; i < N; i++ ) {
		min = clbk.call( thisArg, x[ ix ], i, ix, x );
		if ( min === min && min !== void 0 ) {
			break;
		}
		ix += stride;
	}
	if ( i === N ) {
		return NaN;
	}
	max = min;
	i += 1;
	for ( i; i < N; i++ ) {
		ix += stride;
		v = clbk.call( thisArg, x[ ix ], i, ix, x );
		if ( v === void 0 || isnan( v ) ) {
			continue;
		}
		if ( v < min ) {
			min = v;
		} else if ( v > max ) {
			max = v;
		}
	}
	return max - min;
}


// EXPORTS //
export default nanrangeBy;
